/**
 * The button, in the four emphasis levels the design system allows.
 *
 * daisyUI spreads emphasis across `color` and `variant`, so nothing in the
 * markup shows whether a view has one filled button or three. One `variant`
 * prop, ordered by emphasis, makes the single-primary-action rule reviewable.
 *
 * `loading` is a prop rather than a spinner each caller composes, so the wait
 * is never silent to a screen reader.
 *
 * @module components/ui/Button
 */

import React, { type ComponentPropsWithoutRef, type ReactNode, useId } from "react";

import { Button as DaisyButton } from "react-daisyui";
import { Link } from "react-router";

import { useEditLock, useLockedRefusal } from "./EditLock";
import { Icon } from "./Icon";

/** Sizes a button may take. Mirrored by an `@source inline` entry in style.css. */
export const BUTTON_SIZES = ["xs", "sm", "md", "lg"] as const;
export type ButtonSize = (typeof BUTTON_SIZES)[number];

/** Emphasis levels, highest first. Exactly one `primary` belongs on a view. */
export const BUTTON_VARIANTS = ["primary", "outline", "ghost", "link", "title"] as const;
export type ButtonVariant = (typeof BUTTON_VARIANTS)[number];

/** Written out, not interpolated: Tailwind only generates classes it can read. */
const REASON_PLACEMENT_CLASS = {
  top: "tooltip-top",
  bottom: "tooltip-bottom",
  left: "tooltip-left",
  right: "tooltip-right",
} as const;

export interface ButtonProps
  extends
    Omit<ComponentPropsWithoutRef<"button">, "type" | "color">,
    Pick<ComponentPropsWithoutRef<"a">, "href" | "target" | "rel"> {
  /**
   * Renders as a different element, styled the same: `a` for a link that
   * leaves the page, `label` for the file-input trigger it wraps. Defaults to
   * `button`, which is what every other caller wants.
   */
  tag?: "button" | "a" | "label";
  /** Emphasis. One `primary` per view; everything else steps down from there. */
  variant?: ButtonVariant;
  /** `danger` recolours the button for a destructive action. */
  tone?: "default" | "danger";
  size?: ButtonSize;
  /**
   * Swaps the leading icon for a spinner and refuses activation. It stays
   * focusable and `aria-disabled` rather than `disabled`, so it does not
   * carry the form or validation semantics of a disabled control.
   */
  loading?: boolean;
  /** Announced while `loading`, so the wait is not silent. Defaults to "Working". */
  loadingLabel?: string;
  /** Phosphor icon name, before the label. */
  icon?: string;
  /** Phosphor icon name, after the label. */
  iconEnd?: string;
  /**
   * Refuses activation but stays focusable, as `aria-disabled` plus a click
   * guard: a native `disabled` leaves the tab order and takes its reason along.
   */
  disabled?: boolean;
  /** Why it is disabled: a tooltip on hover and focus, and its description. */
  disabledReason?: string;
  /** Where the reason opens, for a button whose container clips its overflow. */
  disabledReasonPlacement?: "top" | "bottom" | "left" | "right";
  /** Square, icon-only. Requires `aria-label`; see {@link IconButton}. */
  square?: boolean;
  children?: ReactNode;
  /** Defaults to `button`: a bare `<button>` in a form submits it by accident. */
  type?: "button" | "submit";
  /** For a disclosure that has to put focus back on its own trigger. */
  ref?: React.Ref<HTMLButtonElement>;
  "data-testid"?: string;
}

/**
 * daisyUI carries emphasis across two props; this is the whole mapping.
 *
 * Emphasis and tone are decided separately. A destructive action takes the
 * emphasis its frequency warrants, filled in a confirmation dialog and ghost on
 * a row, so `danger` recolours the button and never promotes it.
 */
function appearance(
  variant: ButtonVariant,
  tone: "default" | "danger",
): { color?: "primary" | "ghost" | "error"; daisyVariant?: "outline" | "link"; extra?: string } {
  const danger = tone === "danger";
  switch (variant) {
    case "primary":
      return { color: danger ? "error" : "primary" };
    case "outline":
      return { color: danger ? "error" : undefined, daisyVariant: "outline" };
    case "ghost":
      // `btn-ghost` and `btn-error` together would fill the button, so the
      // colour goes on the text and the button keeps the ghost treatment.
      return { color: "ghost", extra: danger ? "text-error-strong" : undefined };
    case "link":
      return { color: danger ? "error" : undefined, daisyVariant: "link" };
    case "title":
      // A record's title that opens it: ink at rest, underlined on hover only,
      // with none of the button's height or padding.
      return {
        daisyVariant: "link",
        extra:
          "h-auto min-h-0 justify-start p-0 text-left font-semibold text-base-content no-underline hover:underline",
      };
    default:
      return {};
  }
}

/**
 * DaisyButton's own type infers its attributes from a single literal `tag`,
 * which fits none of the three `Button` accepts at once. This alias trades
 * that inference for a loose prop bag at the one call site that needs it.
 */
const PolymorphicButton = DaisyButton as unknown as React.ComponentType<
  Record<string, unknown> & { tag?: ButtonProps["tag"] }
>;

/**
 * @example
 * ```tsx
 * <Button variant="primary" icon="plus" onClick={create}>New programme</Button>
 * <Button variant="ghost" tone="danger" loading={removing}>Remove</Button>
 * ```
 */
export function Button({
  tag = "button",
  variant = "outline",
  tone = "default",
  size = "md",
  loading = false,
  loadingLabel = "Working",
  icon = undefined,
  iconEnd = undefined,
  square = false,
  type = "button",
  disabled: ownDisabled = false,
  className = "",
  children = undefined,
  "data-testid": testId,
  ...props
}: ButtonProps): React.ReactElement | null {
  // Left in the rest so its presence is readable: a caller that can refuse
  // with a reason keeps one wrapper throughout, so the button is not
  // remounted, and does not lose focus, as the reason comes and goes.
  const { disabledReason: ownReason, disabledReasonPlacement = "top", ...rest } = props;
  const locked = useLockedRefusal(ownDisabled, props);
  // An anchor leaves the page and writes nothing, so a lock never refuses it.
  const { disabled, offersReason, disabledReason } =
    tag === "a"
      ? {
          disabled: ownDisabled,
          offersReason: "disabledReason" in props,
          disabledReason: ownReason,
        }
      : locked;
  const reasonId = useId();
  const lockedOut = useEditLock().locked && tag !== "a";
  const { color, daisyVariant, extra } = appearance(variant, tone);
  // A refusal is the caller's own, however it is spelled; working is not one,
  // so a reason belongs to the first and never to the second.
  const refused = disabled || rest["aria-disabled"] === true || rest["aria-disabled"] === "true";
  const unavailable = refused || loading;
  const reason = refused && disabledReason ? disabledReason : undefined;
  const describedBy =
    [rest["aria-describedby"], reason ? reasonId : undefined].filter(Boolean).join(" ") ||
    undefined;
  // daisyUI fades an aria-disabled button, which a reader who has been told
  // why still has to read. `text-muted` is the vetted token for that, and only
  // with a reason: a refusal that says nothing stays faded.
  const classes = [square ? "btn-square" : "", reason ? "text-muted" : "", extra ?? "", className]
    .filter(Boolean)
    .join(" ");

  // An action the lock refuses is left off rather than shown greyed: the
  // section already says why, and a dead button invites the press it refuses.
  if (lockedOut) {
    return null;
  }

  const button = (
    // DaisyButton infers its attribute type from a single literal `tag`; a
    // caller here can pass any of the three, which no one inferred `T` fits.
    // The polymorphic call goes through this loosely typed alias instead of
    // fighting that generic for a union it was not built to accept.
    <PolymorphicButton
      {...rest}
      tag={tag}
      // A `type` attribute means something else on an anchor and nothing on a
      // label; it belongs to the actual `<button>` case only.
      type={tag === "button" ? type : undefined}
      color={color}
      variant={daisyVariant}
      size={size === "md" ? undefined : size}
      // Never the native attribute: a disabled button leaves the tab order, so
      // focus drops to the body mid-press and a reason beside it is never met.
      // `aria-disabled` keeps it focusable; the click guard refuses the press.
      aria-describedby={describedBy}
      // The caller's own value survives: this is declared after the `...rest`
      // spread, so writing it unconditionally silently dropped an
      // `aria-disabled` passed in from outside — the attribute never reached
      // the DOM and the control announced itself as available.
      aria-disabled={unavailable || undefined}
      // `unavailable`, not `loading`: aria-disabled keeps the control
      // focusable, so this guard is the only thing that refuses the press, and
      // a caller setting the attribute itself got the announcement without it.
      onClick={(event: React.MouseEvent<HTMLElement>) => {
        if (unavailable) {
          // Stopped as well as prevented: the button stays focusable, so Enter
          // on it would otherwise still open a clickable row around it.
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        // `rest.onClick` is typed for the `button` case; the loose alias above
        // is what lets this same handler serve `a` and `label` too.
        rest.onClick?.(event as React.MouseEvent<HTMLButtonElement>);
      }}
      // aria-busy rather than only a spinner: the visual swap says nothing to a
      // screen reader, which keeps announcing the idle label.
      // Folded in the same way as `aria-disabled` above: a caller managing its
      // own in-flight state without the `loading` prop would otherwise have it
      // discarded by this later declaration and announce itself idle.
      aria-busy={loading || rest["aria-busy"] || undefined}
      // The wait goes into the name, not beside it. An `aria-label` replaces
      // an element's descendant text in the accessible-name computation, so
      // the sr-only label below is never announced on a labelled button: the
      // name would stay "Save" for the whole action.
      aria-label={
        loading && rest["aria-label"]
          ? `${rest["aria-label"]}, ${loadingLabel}`
          : rest["aria-label"]
      }
      className={classes || undefined}
      data-testid={testId}
    >
      {loading ? (
        <>
          <span className="loading loading-spinner loading-xs" aria-hidden="true" />
          <span className="sr-only">{loadingLabel}</span>
        </>
      ) : (
        icon && <Icon name={icon} aria-hidden />
      )}
      {children}
      {!loading && iconEnd && <Icon name={iconEnd} aria-hidden />}
    </PolymorphicButton>
  );
  if (!offersReason) {
    return button;
  }
  return (
    <span
      className={reason ? `tooltip ${REASON_PLACEMENT_CLASS[disabledReasonPlacement]}` : "contents"}
      data-tip={reason}
    >
      {button}
      {reason ? (
        <span id={reasonId} className="sr-only">
          {reason}
        </span>
      ) : null}
    </span>
  );
}

export interface IconButtonProps extends Omit<ButtonProps, "children" | "square" | "iconEnd"> {
  icon: string;
  /** Required: an icon alone names nothing. */
  "aria-label": string;
}

/**
 * A square, icon-only button. The accessible name is required by the type, so a
 * nameless icon button cannot compile.
 */
export function IconButton({
  icon,
  variant = "ghost",
  ...rest
}: IconButtonProps): React.ReactElement {
  // The loading name is folded in `Button`, which every labelled button needs
  // and not only this one.
  return <Button {...rest} variant={variant} icon={icon} square />;
}

/** The class list a link takes to look like a {@link Button}. */
function linkClasses(
  variant: ButtonVariant,
  tone: "default" | "danger",
  size: ButtonSize,
  className: string,
): string {
  const { color, daisyVariant, extra } = appearance(variant, tone);
  return [
    "btn",
    color ? `btn-${color}` : "",
    daisyVariant ? `btn-${daisyVariant}` : "",
    size === "md" ? "" : `btn-${size}`,
    extra ?? "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
}

export interface LinkButtonProps extends Omit<
  ComponentPropsWithoutRef<"a">,
  "href" | "color" | "children"
> {
  /** Where it goes. A destination, not an action: use {@link Button} for those. */
  to: string;
  /**
   * Loads the document instead of routing to it, for a link out of an error
   * boundary: client-side navigation re-renders the tree that just threw.
   */
  reloadDocument?: boolean;
  variant?: ButtonVariant;
  tone?: "default" | "danger";
  size?: ButtonSize;
  icon?: string;
  iconEnd?: string;
  children?: ReactNode;
  "data-testid"?: string;
}

/**
 * A link that looks like a button.
 *
 * It stays an `<a>`, so it opens in a new tab, copies its address and reads as
 * a link. A `<button>` that calls `navigate()` does none of those.
 */
export function LinkButton({
  to,
  reloadDocument = false,
  variant = "outline",
  tone = "default",
  size = "md",
  icon = undefined,
  iconEnd = undefined,
  className = "",
  children = undefined,
  "data-testid": testId,
  ...rest
}: LinkButtonProps): React.ReactElement {
  const classes = linkClasses(variant, tone, size, className);

  return (
    <Link
      {...rest}
      to={to}
      reloadDocument={reloadDocument}
      className={classes}
      data-testid={testId}
    >
      {icon && <Icon name={icon} aria-hidden />}
      {children}
      {iconEnd && <Icon name={iconEnd} aria-hidden />}
    </Link>
  );
}

export interface ExternalLinkButtonProps
  // `iconEnd` too: this draws `arrow-square-out` at the end whatever a caller
  // asks for, and the prop was never destructured, so it reached the anchor as
  // an unknown DOM attribute.
  extends Omit<LinkButtonProps, "to" | "reloadDocument" | "iconEnd"> {
  /** An absolute address outside the app. */
  href: string;
}

/**
 * A link out of the app, in a new tab, that looks like a button.
 *
 * Not a {@link LinkButton}: an external address has no route to resolve, and
 * the new tab is announced so it is not a surprise.
 */
export function ExternalLinkButton({
  href,
  variant = "outline",
  tone = "default",
  size = "md",
  icon = undefined,
  className = "",
  children = undefined,
  "data-testid": testId,
  ...rest
}: ExternalLinkButtonProps): React.ReactElement {
  return (
    <a
      {...rest}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={linkClasses(variant, tone, size, className)}
      data-testid={testId}
    >
      {icon && <Icon name={icon} aria-hidden />}
      {children} <span className="sr-only">(opens in a new tab)</span>
      <Icon name="arrow-square-out" aria-hidden />
    </a>
  );
}
